// File: Componates/RoomsSection.jsx
import React from "react";
import PropTypes from "prop-types";
import { formatValue, formatExtraCharge } from "./utils/checkoutTableUtils";

const TYPE_BADGE = {
  ROOM: "bg-primary-subtle text-primary",
  DORMITORY: "bg-info-subtle text-info"
};

const AccommodationBadge = ({ type }) => (
  <span className={`badge rounded-pill ${TYPE_BADGE[type] || "bg-secondary-subtle text-secondary"}`}>
    {type || "-"}
  </span>
);

/**
 * Resolve a single cell's display value.
 *
 * DORMITORY rows:
 *  - Extra Bed/Person/Child are always null → render "-".
 *  - `Rate` (room_rate / fallback rate_per_bed) is shown multiplied against
 *    beds_no, e.g. "₹250.00 × 2 = ₹500.00", via formatExtraCharge.
 *  - `Complementary` (complementary_price) is likewise shown multiplied
 *    against beds_no, e.g. "₹100.00 × 2 = ₹200.00".
 *  - `Beds Booked` (bedsKey) shows beds_no as a plain number.
 *
 * ROOM rows:
 *  - Extra Bed/Person/Child render as usual "count × rate = total".
 *  - Rate and Complementary render as plain currency values.
 *  - `Beds Booked` renders "-".
 */
const renderCell = (room, col) => {
  const isDorm = room.accommodation_type === "DORMITORY";

  // Extra Bed / Extra Person / Extra Child
  if (col.countKey && col.rateKey) {
    if (isDorm) return "-";
    return formatExtraCharge(room[col.countKey], room[col.rateKey]);
  }

  // Beds Booked
  if (col.bedsKey) {
    if (!isDorm) return "-";
    return formatValue(room[col.bedsKey], col.format);
  }

  // Rate → for dorm rows, multiply rate_per_bed by beds_no
  if (col.key === "room_rate") {
    if (isDorm) {
      return formatExtraCharge(room.beds_no, room.rate_per_bed);
    }
    return formatValue(room.room_rate, col.format);
  }

  // Complementary → for dorm rows, multiply complementary_price by beds_no
  if (col.key === "complementary_price") {
    if (isDorm) {
      return formatExtraCharge(room.beds_no, room.complementary_price);
    }
    return formatValue(room.complementary_price, col.format);
  }

  const raw = room[col.key] ?? (col.fallbackKey ? room[col.fallbackKey] : undefined);
  return formatValue(raw, col.format);
};

/**
 * `grandTotal` is supplied by the backend (`data.rooms_total` from
 * getCheckOutDetails) — this component only renders, it never sums.
 */
const RoomsSection = ({ rooms = [], columns = [], grandTotal = 0 }) => {
  return (
    <div className="col-12">
      <div className="table-responsive w-100">
        <table className="table table-bordered ">
          <thead>
            <tr>
              <th colSpan={columns.length} className="text-center bg-light">
                Rooms ({rooms.length})
              </th>
            </tr>
            <tr>
              {columns.map((c) => (
                <th key={c.label}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rooms.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="text-center text-muted py-3">
                  No rooms found
                </td>
              </tr>
            )}

            {rooms.map((room) => (
              <tr key={room.book_details_id}>
                {columns.map((c) => {
                  if (c === columns[0]) {
                    return (
                      <td key={c.label} className="d-flex align-items-center gap-2">
                        <div className="fw-semibold">{room.room_no}</div>
                        <AccommodationBadge type={room.accommodation_type} />
                      </td>
                    );
                  }
                  return <td key={c.label}>{renderCell(room, c)}</td>;
                })}
              </tr>
            ))}
          </tbody>

          {rooms.length > 0 && (
            <tfoot>
              <tr className="table-light fw-bold">
                <td colSpan={columns.length - 1} className="text-end">
                  Grand Total
                </td>
                <td className="text-end">{formatValue(grandTotal, "currency")}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

RoomsSection.propTypes = {
  rooms: PropTypes.array,
  columns: PropTypes.array,
  grandTotal: PropTypes.number
};

export default RoomsSection;